import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Upload, Image, Video, Send, Clock, Plus, Check, X, 
  Loader2, Instagram, AlertCircle, Calendar, ChevronDown, Target,
  LogOut, History, User, Menu, Trash2, Scissors, Zap, Link as LinkIcon, BarChart3
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AppDownloadBadges } from '../layout/Header';
import { API_URL } from '../../lib/apiConfig';
import { TikTokPublishOptions, tiktokConfig } from '../TikTokPublishOptions';
import { readConnectionResponse } from '../../lib/connectionResponse';
import VizardScheduler from '../VizardScheduler';

// Safe localStorage wrapper for Safari/iOS compatibility
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
};

// Platform icons (resilient to case/alias — Chat33 may return TIKTOK, X, twitter, etc.)
const PlatformIcon = ({ platform, size = 20 }) => {
  const key = ((platform || '') + '').toLowerCase().trim();
  const normalized = key === 'x' ? 'twitter' : key;
  const icons = {
    instagram: <Instagram size={size} className="text-pink-500" />,
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-white">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
      </svg>
    ),
    youtube: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-red-500">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-blue-500">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-white">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-blue-600">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    threads: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-white">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.024.898-.748 2.176-1.211 3.693-1.34a15.1 15.1 0 012.005-.045c-.086-.655-.32-1.19-.696-1.587-.473-.5-1.186-.762-2.062-.762h-.036c-.94.008-1.75.31-2.282.852l-1.44-1.44c.876-.876 2.14-1.358 3.563-1.358h.057c1.39.012 2.539.451 3.42 1.306.764.741 1.236 1.729 1.406 2.942.483.058.948.142 1.39.252 1.334.333 2.455.903 3.336 1.697 1.11.999 1.846 2.315 2.186 3.918.348 1.64.193 3.418-.46 5.286-.617 1.765-1.677 3.36-3.064 4.612-2.09 1.886-4.864 2.88-8.023 2.88h-.058zm.559-9.928c-1.088.066-1.97.343-2.553.8-.46.361-.693.81-.674 1.3.02.468.253.866.694 1.184.515.373 1.24.567 2.04.546 1.114-.058 1.98-.408 2.575-1.04.485-.515.8-1.2.941-2.04a9.52 9.52 0 00-3.023-.75z"/>
      </svg>
    )
  };
  return icons[normalized] || <div className="w-5 h-5 bg-gray-500 rounded-full" title={`unknown platform: ${platform}`} />;
};

// Timezone options — "auto" uses the browser's real offset (handles BST/DST automatically)
const timezones = [
  { value: 'auto',      label: 'Auto · Your local time' },
  { value: 'GMT+00:00', label: 'UTC / GMT+00:00' },
  { value: 'GMT+01:00', label: 'GMT+01:00 (BST, Paris, Berlin)' },
  { value: 'GMT-05:00', label: 'GMT-05:00 (New York EST)' },
  { value: 'GMT-08:00', label: 'GMT-08:00 (Los Angeles PST)' },
];

// Returns the browser's current UTC-offset string in ISO format, e.g. "+01:00" or "-05:00".
const browserOffsetIso = () => {
  const m = -new Date().getTimezoneOffset(); // minutes east of UTC
  const sign = m >= 0 ? '+' : '-';
  const abs = Math.abs(m);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

const DashboardPage = () => {
  const { user, isAuthenticated, setShowAuthModal, logout } = useAuth();
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [tiktokMode, setTiktokMode] = useState('DIRECT_POST');
  const [tiktokPrivacy, setTiktokPrivacy] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishInFlight = useRef(false);
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('07:00');
  const [timezone, setTimezone] = useState('auto');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [message, setMessage] = useState(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const connectInFlight = useRef(false);
  const [connectError, setConnectError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [postHistory, setPostHistory] = useState([]);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeView, setActiveView] = useState('quick'); // quick | vizard | connections | analytics
  const hasTikTok = accounts.some(a => selectedAccounts.includes(a.id) && (a.platform || '').toLowerCase() === 'tiktok');
  const draftUpload = hasTikTok && tiktokMode === 'MEDIA_UPLOAD';

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setScheduleDate(today);
    
    // Handle the /api/social/callback → /dashboard bounce
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const platform = urlParams.get('platform');
    const status = urlParams.get('status');
    const err = urlParams.get('error');
    
    if (connected === '1' || connected === 'true') {
      window.history.replaceState({}, document.title, window.location.pathname);
      const label = platform ? (platform === 'x' ? 'X (Twitter)' : platform) : 'Account';
      setMessage({ type: 'success', text: `${label} connected — refresh your accounts below.` });
      if (isAuthenticated) fetchAccounts();
    } else if (connected === '0' || err) {
      window.history.replaceState({}, document.title, window.location.pathname);
      setMessage({ type: 'error', text: `Connect failed${err ? `: ${err}` : ''}.` });
    } else if (urlParams.get('oauth') === 'success' && platform) {
      // Legacy Meta redirect (kept for back-compat)
      window.history.replaceState({}, document.title, window.location.pathname);
      setMessage({ type: 'success', text: `${platform} account connected successfully!` });
      if (isAuthenticated) fetchAccounts();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAccounts();
      fetchPostHistory();
    }
  }, [isAuthenticated]);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const token = safeStorage.getItem('autopostr_token');
      const response = await fetch(`${API_URL}/api/social/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const text = await response.text();
      if (response.ok && text) {
        try {
          const data = JSON.parse(text);
          setAccounts(data.accounts || []);
        } catch (e) {
          console.error('Error parsing accounts:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
    setLoadingAccounts(false);
  };

  const disconnectAccount = async (accountId, platform, username) => {
    if (!window.confirm(`Disconnect ${platform} account "${username}"? You will need to reconnect to post again.`)) {
      return;
    }
    try {
      const token = safeStorage.getItem('autopostr_token');
      const response = await fetch(`${API_URL}/api/social/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Disconnect failed');
      setMessage({ type: 'success', text: `${platform} account disconnected.` });
      // Remove from selected + refresh
      setSelectedAccounts(prev => prev.filter(id => id !== accountId));
      fetchAccounts();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      setMessage({ type: 'error', text: `Failed to disconnect: ${error.message}` });
    }
  };

  const fetchPostHistory = async () => {
    try {
      const token = safeStorage.getItem('autopostr_token');
      const response = await fetch(`${API_URL}/api/posts/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const text = await response.text();
      if (response.ok && text) {
        try {
          const data = JSON.parse(text);
          setPostHistory(data.posts || []);
        } catch (e) {
          console.error('Error parsing post history:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching post history:', error);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || e.target?.files || []);
    const validFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    setMediaFiles(prev => [...prev, ...validFiles]);
  }, []);

  const removeMedia = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    );
  };

  const uploadMedia = async (file) => {
    const token = safeStorage.getItem('autopostr_token');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/media/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || 'Upload failed');
    }
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new Error('Invalid response from server');
    }
    if (!data.publicUrl) throw new Error('No publicUrl in response');
    return data.publicUrl;
  };

  const handlePublish = async () => {
    if (publishing || publishInFlight.current) return;
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    // Lock publishing for non-Pro users → redirect to pricing
    if (!user?.is_pro && !user?.is_admin) {
      setMessage({ type: 'error', text: 'Pro subscription required to publish. Redirecting to pricing…' });
      setTimeout(() => { window.location.href = '/pricing'; }, 1200);
      return;
    }

    if (!content.trim() && mediaFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please add content or media to publish' });
      return;
    }

    if (selectedAccounts.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one account' });
      return;
    }
    if (hasTikTok && tiktokMode === 'DIRECT_POST' && !tiktokPrivacy) {
      setMessage({ type: 'error', text: 'Select TikTok visibility before publishing.' });
      return;
    }
    if (hasTikTok && !mediaFiles.length) {
      setMessage({ type: 'error', text: 'TikTok requires an image or video.' });
      return;
    }

    publishInFlight.current = true;
    setPublishing(true);
    setMessage(null);

    try {
      const mediaUrls = [];
      console.log(`Starting upload of ${mediaFiles.length} files`);
      
      for (let i = 0; i < mediaFiles.length; i++) {
        console.log(`Uploading file ${i + 1}/${mediaFiles.length}: ${mediaFiles[i].name}`);
        setUploadProgress(`Uploading ${i + 1}/${mediaFiles.length}...`);
        try {
          const url = await uploadMedia(mediaFiles[i]);
          console.log(`Uploaded file ${i + 1}, URL: ${url}`);
          mediaUrls.push(url);
        } catch (uploadErr) {
          console.error(`Failed to upload file ${i + 1}:`, uploadErr);
          throw uploadErr;
        }
      }
      
      console.log(`All uploads complete. Total URLs: ${mediaUrls.length}`, mediaUrls);
      setUploadProgress(draftUpload ? 'Submitting TikTok draft…' : 'Publishing...');

      const token = safeStorage.getItem('autopostr_token');
      let scheduleTimeString = null;
      if (scheduleMode === 'later' && scheduleDate && scheduleTime) {
        // "auto" → use browser's real offset (handles BST/DST automatically for UK users).
        // Otherwise use the explicit offset from the dropdown.
        const offset = timezone === 'auto' ? browserOffsetIso() : (timezone.replace('GMT', '') || '+00:00');
        scheduleTimeString = `${scheduleDate}T${scheduleTime}:00${offset}`;
        // Guard: Chat33 silently drops schedules that are too close to now (needs ~5min lead).
        const whenMs = new Date(scheduleTimeString).getTime();
        if (whenMs - Date.now() < 5 * 60 * 1000) {
          setMessage({ type: 'error', text: 'Please pick a schedule time at least 5 minutes in the future.' });
          setPublishing(false);
          setUploadProgress(null);
          return;
        }
      }

      const publishPayload = {
        content,
        media_urls: mediaUrls,
        account_ids: selectedAccounts,
        publish_now: scheduleMode === 'now',
        schedule_time: scheduleTimeString,
        ...(hasTikTok ? { platform_config: tiktokConfig(tiktokMode, tiktokPrivacy) } : {}),
      };
      
      console.log('Publishing with payload:', publishPayload);

      const response = await fetch(`${API_URL}/api/posts/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(publishPayload)
      });

      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = { error: responseText };
      }

      if (!response.ok) {
        // 403 = Pro subscription required → redirect to pricing
        if (response.status === 403 && data.detail === 'Pro subscription required.') {
          setMessage({ type: 'error', text: 'Pro subscription required. Redirecting to pricing…' });
          setTimeout(() => { window.location.href = '/pricing'; }, 1200);
          return;
        }
        throw new Error(data.detail || data.error || 'Failed to publish');
      }

      setMessage({ 
        type: 'success', 
        text: draftUpload
          ? (scheduleMode === 'now' ? 'Submitted for TikTok draft upload. Finish publishing in TikTok once it arrives.' : 'TikTok draft upload scheduled. Finish publishing in TikTok after delivery.')
          : (scheduleMode === 'now' ? 'Post submitted for publishing.' : 'Post scheduled successfully!')
      });
      
      setContent('');
      setMediaFiles([]);
      setSelectedAccounts([]);
      setTiktokMode('DIRECT_POST');
      setTiktokPrivacy('');
      fetchPostHistory();

    } catch (error) {
      console.error('Publish error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to publish' });
    } finally {
      publishInFlight.current = false;
      setUploadProgress(null);
      setPublishing(false);
    }
  };

  const connectAccount = async (platform) => {
    if (connectInFlight.current) return;
    setShowAccountDropdown(false);
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    const supported = ['instagram', 'facebook', 'tiktok', 'youtube', 'x', 'linkedin'];
    if (!supported.includes(platform)) {
      setMessage({ type: 'error', text: `${platform === 'x' ? 'X (Twitter)' : platform} isn't supported yet.` });
      return;
    }
    const label = platform === 'x' ? 'X (Twitter)' : platform === 'linkedin' ? 'LinkedIn' : (platform.charAt(0).toUpperCase() + platform.slice(1));
    connectInFlight.current = true;
    setConnectingPlatform(platform);
    setConnectError('');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const token = safeStorage.getItem('autopostr_token');
      const res = await fetch(`${API_URL}/api/social/connect/${platform}`, {
        signal: controller.signal,
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const data = await readConnectionResponse(res);
      setMessage({ type: 'success', text: `Opening ${label} sign-in — approve there and you'll come right back.` });
      // Same-tab redirect so the OAuth "return" seamlessly lands the user
      // back on /dashboard via our /api/social/callback bounce.
      window.location.href = data.authUrl;
    } catch (error) {
      const detail = error.name === 'AbortError' ? 'The connection request timed out. Please try again shortly.' : error.message;
      setConnectError(`Failed to connect ${label}: ${detail}`);
    } finally {
      clearTimeout(timeout);
      connectInFlight.current = false;
      setConnectingPlatform(null);
    }
  };

  // Handle platform selection from dropdown
  const handlePlatformSelect = (platform) => {
    connectAccount(platform);
  };

  const totalPosts = user?.total_posts || 0;

  // Format subscription expiry date for display
  const formatExpiry = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return null;
    }
  };
  const expiryDate = formatExpiry(user?.pro_expires);

  // User Dashboard View
  if (showDashboard && isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <header className="bg-[#111] border-b border-white/10">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/j0h273d6_auto.png" alt="AutoPostr" className="h-20 md:h-28 lg:h-32 w-auto" />
            </a>
            <h2 className="hidden md:block text-xl lg:text-2xl font-semibold text-white">Simplify Content Posting.</h2>
            <div className="flex items-center gap-3 sm:gap-4">
              {/* App Store + Play Store badges */}
              <div className="hidden md:block">
                <AppDownloadBadges compact />
              </div>
              {/* Account email displayed at top-right */}
              <div className="hidden sm:flex flex-col items-end" data-testid="dashboard-account-email">
                <span className="text-white/40 text-[11px] uppercase tracking-wider">Account</span>
                <span className="text-white font-medium text-sm truncate max-w-[200px]">{user?.email}</span>
              </div>
              <button onClick={() => setShowDashboard(false)} className="px-4 py-2 rounded-full bg-emerald-500 text-white font-medium">
                New Post
              </button>
              <button onClick={logout} className="px-4 py-2 rounded-full bg-white/10 text-white/60 hover:text-white">
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-bold text-white mb-2">My Dashboard</h1>
          {/* Account email shown again on mobile (where header version is hidden) */}
          <p className="sm:hidden text-white/50 text-sm mb-6" data-testid="dashboard-account-email-mobile">
            Signed in as <span className="text-white font-medium">{user?.email}</span>
          </p>
          <div className="hidden sm:block mb-8" />
          
          {/* Quick Actions */}
          <div className={`grid grid-cols-1 ${user?.is_pro ? '' : 'md:grid-cols-2'} gap-4 mb-8`}>
            <button 
              onClick={() => setShowDashboard(false)} 
              data-testid="create-new-post-card"
              className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 hover:border-emerald-500/50 transition-all group text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <Send size={24} className="text-emerald-400" />
                <span className="text-white font-semibold text-lg">Create New Post</span>
              </div>
              <p className="text-white/50 text-sm">Upload media, write caption, and publish to your accounts</p>
            </button>
            
            {!user?.is_pro && (
              <a 
                href="/pricing" 
                data-testid="bulk-schedule-upsell-card"
                className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-600/10 border border-blue-500/30 hover:border-blue-500/50 transition-all group text-left block"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Calendar size={24} className="text-blue-400" />
                  <span className="text-white font-semibold text-lg">Bulk Schedule for next 30 days</span>
                </div>
                <p className="text-white/50 text-sm">Plan and schedule your content calendar in advance</p>
              </a>
            )}
          </div>

          {/* Stats Card - Just Total Posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="p-6 rounded-2xl bg-[#111] border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <History size={20} className="text-purple-400" />
                <span className="text-white/50">Total Posts</span>
              </div>
              <p className="text-3xl font-bold text-white">{totalPosts}</p>
            </div>
            <div className={`p-6 rounded-2xl ${user?.is_pro ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`} data-testid="dashboard-subscription-card">
              <div className="flex items-center gap-3 mb-2">
                <Check size={20} className={user?.is_pro ? 'text-yellow-400' : 'text-emerald-400'} />
                <span className={`font-medium ${user?.is_pro ? 'text-yellow-400' : 'text-emerald-400'}`}>
                  {user?.is_pro ? 'AutoPostr Pro' : 'Free Plan'}
                </span>
              </div>
              <p className="text-white/60 text-sm">
                {user?.is_pro
                  ? expiryDate
                    ? <>Subscription renews on <span className="text-white font-medium">{expiryDate}</span></>
                    : 'Active subscription'
                  : <>Upgrade to <a href="/pricing" className="text-emerald-400 underline">AutoPostr Pro</a> to publish unlimited posts</>}
              </p>
            </div>
          </div>

          {/* Connected Accounts */}
          <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden mb-8">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Connected Accounts</h2>
              <button 
                onClick={() => setShowDashboard(false)} 
                className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm hover:bg-emerald-500/20"
              >
                + Add Account
              </button>
            </div>
            {loadingAccounts ? (
              <div className="p-12 text-center">
                <Loader2 size={32} className="mx-auto text-white/20 animate-spin mb-4" />
                <p className="text-white/50">Loading accounts...</p>
              </div>
            ) : accounts.length === 0 ? (
              <div className="p-12 text-center">
                <User size={48} className="mx-auto text-white/20 mb-4" />
                <p className="text-white/50 mb-2">No accounts connected yet</p>
                <p className="text-white/30 text-sm">Click "Add Account" to connect your social media</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {accounts.map((account, idx) => (
                  <div key={idx} className="p-4 flex items-center gap-4 hover:bg-white/[0.02]">
                    <PlatformIcon platform={account.platform} size={28} />
                    <div className="flex-1">
                      <p className="text-white font-medium">{account.username || account.name}</p>
                      <p className="text-white/40 text-sm capitalize">{account.platform}</p>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                      <span className="text-emerald-400 text-xs">Connected</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Post History */}
          <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">Recent Posts</h2>
            </div>
            {postHistory.length === 0 ? (
              <div className="p-12 text-center">
                <History size={48} className="mx-auto text-white/20 mb-4" />
                <p className="text-white/50">No posts yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {postHistory.slice(0, 10).map((post, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/[0.02]" data-testid={`history-post-${post.id || idx}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-white line-clamp-2">{post.content || 'Media post'}</p>
                      <span className={`text-xs px-2 py-0.5 rounded shrink-0 capitalize ${
                        post.status === 'published' ? 'bg-emerald-500/10 text-emerald-400'
                        : post.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400'
                        : post.status === 'publishing' ? 'bg-amber-500/10 text-amber-400'
                        : post.status === 'failed' ? 'bg-red-500/10 text-red-400'
                        : 'bg-white/10 text-white/50'}`} data-testid={`history-status-${post.id}`}>{post.status === 'draft_uploaded' ? 'In TikTok drafts' : post.status || 'draft'}</span>
                      {post.tiktok_publish_mode && <span data-testid={`history-tiktok-mode-${post.id}`} className="text-xs text-white/60">{post.tiktok_publish_mode === 'MEDIA_UPLOAD' ? 'TikTok draft upload' : 'TikTok direct publish'}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-white/40 text-sm">
                      <span>
                        {post.status === 'scheduled' && (post.schedule_time || post.scheduled_at)
                          ? `Scheduled for ${new Date(post.schedule_time || post.scheduled_at).toLocaleString()}`
                          : new Date(post.published_at || post.schedule_time || post.scheduled_at || post.created_at).toLocaleString()}
                      </span>
                      <span>{(post.account_ids || post.platforms)?.length || 0} account(s)</span>
                    </div>
                    {post.error_message && <p className="text-red-400/80 text-xs mt-1 line-clamp-2">{post.error_message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-[#111] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 flex-shrink-0">
            <img src="https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/j0h273d6_auto.png" alt="AutoPostr" className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto" />
          </a>
          
          <div className="hidden lg:block">
            <h2 className="text-lg xl:text-xl font-semibold text-white">Simplify Content Posting.</h2>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-2 sm:gap-3">
            {/* App Store + Play Store badges - desktop only */}
            <div className="hidden md:block mr-1">
              <AppDownloadBadges compact />
            </div>
            {isAuthenticated ? (
              <>
                {user?.is_admin && (
                  <span className="px-2 sm:px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-400 text-xs font-medium">
                    Admin
                  </span>
                )}
                
                {/* PRO / Free Badge */}
                {user?.is_pro ? (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/30">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#EAB308" stroke="#EAB308" strokeWidth="2"/>
                    </svg>
                    <span className="text-yellow-400 font-medium text-sm">PRO</span>
                  </div>
                ) : (
                  <a href="/pricing" className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors">
                    <span className="text-white/60 font-medium text-sm">Upgrade to PRO</span>
                  </a>
                )}

                {/* User Menu - Desktop */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    onTouchEnd={(e) => { e.preventDefault(); setShowUserMenu(!showUserMenu); }}
                    className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation"
                  >
                    <User size={18} className="text-white/60" />
                    <ChevronDown size={14} className="text-white/40" />
                  </button>
                  
                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} onTouchEnd={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 top-full mt-2 w-52 bg-[#1a1a1a] border border-white/10 rounded-xl p-2 z-50 shadow-2xl">
                        <button
                          onClick={() => { setShowDashboard(true); setShowUserMenu(false); }}
                          onTouchEnd={(e) => { e.preventDefault(); setShowDashboard(true); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 active:bg-white/20 text-white/80 text-sm touch-manipulation"
                        >
                          <History size={18} />
                          My Dashboard
                        </button>
                        <hr className="my-2 border-white/10" />
                        <button
                          onClick={() => { logout(); setShowUserMenu(false); }}
                          onTouchEnd={(e) => { e.preventDefault(); logout(); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 active:bg-white/20 text-red-400 text-sm touch-manipulation"
                        >
                          <LogOut size={18} />
                          Sign Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                onTouchEnd={(e) => { e.preventDefault(); setShowAuthModal(true); }}
                className="px-4 py-2 rounded-full bg-white text-black font-medium hover:bg-white/90 active:bg-white/80 transition-colors touch-manipulation"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex sm:hidden items-center gap-2">
            {isAuthenticated && (
              user?.is_pro ? (
                <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/30">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#EAB308" stroke="#EAB308" strokeWidth="2"/>
                  </svg>
                  <span className="text-yellow-400 font-medium text-xs">PRO</span>
                </div>
              ) : (
                <a href="/pricing" className="flex items-center px-2 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <span className="text-white/60 font-medium text-xs">Upgrade</span>
                </a>
              )
            )}
            <button
              onClick={() => isAuthenticated ? setShowMobileMenu(true) : setShowAuthModal(true)}
              onTouchEnd={(e) => { e.preventDefault(); isAuthenticated ? setShowMobileMenu(true) : setShowAuthModal(true); }}
              className="p-3 rounded-full bg-white/5 hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation"
            >
              <Menu size={28} className="text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Full-Screen Menu */}
      {showMobileMenu && isAuthenticated && (
        <div className="fixed inset-0 z-50 bg-[#0a0a0a]">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <span className="text-white font-semibold text-lg">Menu</span>
              <button
                onClick={() => setShowMobileMenu(false)}
                onTouchEnd={(e) => { e.preventDefault(); setShowMobileMenu(false); }}
                className="p-2 rounded-full bg-white/5 active:bg-white/20 touch-manipulation"
              >
                <X size={24} className="text-white" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {/* User Info */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <User size={24} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{user?.name || user?.email}</p>
                    {user?.is_admin && (
                      <span className="text-purple-400 text-xs">Admin</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg ${user?.is_pro ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                    <p className={`text-xs mb-1 ${user?.is_pro ? 'text-yellow-400' : 'text-emerald-400'}`}>Status</p>
                    <p className={`font-bold text-sm ${user?.is_pro ? 'text-yellow-400' : 'text-emerald-400'}`}>
                      {user?.is_pro ? 'PRO' : 'Free'}
                    </p>
                    {user?.is_pro && expiryDate && (
                      <p className="text-white/50 text-[10px] mt-0.5">until {expiryDate}</p>
                    )}
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-white/50 text-xs mb-1">Total Posts</p>
                    <p className="text-white font-bold">{totalPosts}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="space-y-2">
                <button
                  onClick={() => { setShowDashboard(true); setShowMobileMenu(false); }}
                  onTouchEnd={(e) => { e.preventDefault(); setShowDashboard(true); setShowMobileMenu(false); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/20 text-white touch-manipulation"
                >
                  <History size={22} className="text-purple-400" />
                  <span className="font-medium">My Dashboard</span>
                </button>
                
                <button
                  onClick={() => { setShowDashboard(false); setShowMobileMenu(false); }}
                  onTouchEnd={(e) => { e.preventDefault(); setShowDashboard(false); setShowMobileMenu(false); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/20 text-white touch-manipulation"
                >
                  <Send size={22} className="text-emerald-400" />
                  <span className="font-medium">Create New Post</span>
                </button>
                
                {!user?.is_pro && (
                  <a
                    href="/pricing"
                    data-testid="mobile-bulk-schedule-upsell"
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/20 text-white touch-manipulation"
                  >
                    <Calendar size={22} className="text-blue-400" />
                    <span className="font-medium">Bulk Schedule</span>
                  </a>
                )}
              </div>
            </div>
            
            {/* Sign Out at bottom */}
            <div className="p-4 border-t border-white/10">
              <button
                onClick={() => { logout(); setShowMobileMenu(false); }}
                onTouchEnd={(e) => { e.preventDefault(); logout(); setShowMobileMenu(false); }}
                className="w-full flex items-center justify-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 font-medium touch-manipulation"
              >
                <LogOut size={20} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - desktop only */}
          {isAuthenticated && (
            <aside className="hidden lg:block w-60 flex-shrink-0" data-testid="dashboard-sidebar">
              <div className="sticky top-24 space-y-2 bg-[#0f0f0f] border border-white/10 rounded-2xl p-3">
                <SidebarItem icon={<Zap size={16} />} label="Quick Post" active={activeView === 'quick'} onClick={() => setActiveView('quick')} testid="sidebar-quick" />
                <SidebarItem icon={<Scissors size={16} />} label="Upload via Vizard" active={activeView === 'vizard'} onClick={() => setActiveView('vizard')} testid="sidebar-vizard" badge={(user?.is_pro || user?.is_admin) ? null : 'Pro'} />
                <SidebarItem icon={<LinkIcon size={16} />} label="Connections" active={false} onClick={() => { setActiveView('quick'); setTimeout(() => { const el = document.querySelector('[data-testid="account-selector"], #accounts-section'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setShowAccountDropdown(true); }, 50); }} testid="sidebar-connections" />
                <SidebarItem icon={<BarChart3 size={16} />} label="My Dashboard" active={false} onClick={() => setShowDashboard(true)} testid="sidebar-dashboard" />

                <div className="pt-3 mt-3 border-t border-white/5">
                  <p className="px-3 text-white/40 text-[10px] uppercase tracking-wider mb-2">Usage</p>
                  <div className="px-3 py-2 rounded-xl bg-white/[0.03]">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-white/60">Posts</span>
                      <span className="text-white font-semibold">{totalPosts}</span>
                    </div>
                    <div className={`mt-2 text-[11px] ${(user?.is_pro || user?.is_admin) ? 'text-yellow-400' : 'text-emerald-400'}`}>
                      {user?.is_admin ? 'Admin · Pro' : (user?.is_pro ? 'AutoPostr Pro' : 'Free plan')}
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          )}

          <div className="flex-1 min-w-0">
            {/* Mobile view switcher */}
            {isAuthenticated && (
              <div className="lg:hidden mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="dashboard-mobile-tabs">
                {[
                  { k: 'quick', label: 'Quick Post', icon: <Zap size={14} /> },
                  { k: 'vizard', label: 'Vizard', icon: <Scissors size={14} /> },
                ].map(t => (
                  <button key={t.k} onClick={() => setActiveView(t.k)} data-testid={`sidebar-mobile-${t.k}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs whitespace-nowrap ${activeView === t.k ? 'bg-emerald-500 text-white' : 'bg-white/5 text-white/70'}`}>
                    {t.icon}{t.label}
                  </button>
                ))}
              </div>
            )}

            {activeView === 'vizard' && isAuthenticated ? (
              <VizardScheduler accounts={accounts} isPro={!!(user?.is_pro || user?.is_admin)} onScheduled={fetchPostHistory} />
            ) : (
              <div data-testid="quick-post-view">
        {/* Hero Section with Phone Mockups */}
        <div className="mb-8 sm:mb-16">
          <div className="flex justify-center items-end gap-1 sm:gap-2 md:gap-4 mb-6 sm:mb-10 overflow-hidden px-2">
            {/* YouTube Shorts */}
            <div className="w-12 sm:w-20 md:w-28 lg:w-36 transform -rotate-6 translate-y-4">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-red-600/20 to-red-900/20 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-red-500 sm:w-8 sm:h-8">
                        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-2 bg-gray-900/80"><p className="text-white text-[5px] sm:text-[6px] md:text-[8px] font-medium truncate">YouTube</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Instagram */}
            <div className="w-14 sm:w-24 md:w-32 lg:w-40 transform -rotate-3 translate-y-2 z-10">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-orange-500/20 flex items-center justify-center">
                      <Instagram size={24} className="text-pink-500 sm:w-9 sm:h-9" />
                    </div>
                    <div className="p-1 sm:p-2 bg-gray-900/80"><p className="text-white text-[5px] sm:text-[6px] md:text-[8px] font-medium truncate">Instagram</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* TikTok */}
            <div className="w-16 sm:w-28 md:w-36 lg:w-44 z-20">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-cyan-500/20 to-pink-500/20 flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-white sm:w-10 sm:h-10">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-2 bg-gray-900/80"><p className="text-white text-[5px] sm:text-[6px] md:text-[8px] font-medium truncate">TikTok</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Facebook */}
            <div className="w-14 sm:w-24 md:w-32 lg:w-40 transform rotate-3 translate-y-2 z-10">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-blue-600/20 to-blue-900/20 flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500 sm:w-9 sm:h-9">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-2 bg-gray-900/80"><p className="text-white text-[5px] sm:text-[6px] md:text-[8px] font-medium truncate">Facebook</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* X */}
            <div className="w-12 sm:w-20 md:w-28 lg:w-36 transform rotate-6 translate-y-4">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-gray-700/20 to-gray-900/20 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-white sm:w-8 sm:h-8">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-2 bg-gray-900/80"><p className="text-white text-[5px] sm:text-[6px] md:text-[8px] font-medium truncate">X</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center px-4">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4">Post to All Platforms at ONCE</h1>
            <p className="text-sm sm:text-xl text-white/60">
              in One Click 🔥 — <span className="text-emerald-400 font-semibold">FREE</span>
            </p>
          </div>
        </div>

        {/* Main Card */}
        <div className="max-w-4xl mx-auto bg-[#111] border border-white/10 rounded-2xl sm:rounded-3xl overflow-hidden">
          {/* Media Section */}
          <div className="p-4 sm:p-6 border-b border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                <Image size={16} className="text-white/60" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm sm:text-base">Media</h2>
                <p className="text-white/40 text-xs sm:text-sm">Add images or videos</p>
              </div>
            </div>

            {mediaFiles.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-4">
                {mediaFiles.map((file, index) => (
                  <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-white/5">
                    {file.type.startsWith('video/') ? (
                      <video src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                    ) : (
                      <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                    )}
                    <button 
                      onClick={() => removeMedia(index)} 
                      onTouchEnd={(e) => { e.preventDefault(); removeMedia(index); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 active:bg-black touch-manipulation"
                    >
                      <X size={14} className="text-white" />
                    </button>
                    {file.type.startsWith('video/') && <div className="absolute bottom-2 left-2"><Video size={14} className="text-white" /></div>}
                  </div>
                ))}
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 active:border-white/60 flex items-center justify-center cursor-pointer transition-colors touch-manipulation">
                  <Plus size={24} className="text-white/40" />
                  <input type="file" accept="image/*,video/*" multiple onChange={handleDrop} className="hidden" />
                </label>
              </div>
            ) : (
              <label onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} className="block p-6 sm:p-8 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 active:border-white/60 cursor-pointer transition-colors touch-manipulation">
                <div className="text-center">
                  <Upload size={28} className="mx-auto text-white/40 mb-2 sm:mb-3 sm:w-8 sm:h-8" />
                  <p className="text-white font-medium mb-1 text-sm sm:text-base">Tap to upload</p>
                  <p className="text-white/40 text-xs sm:text-sm">Images or Videos</p>
                </div>
                <input type="file" accept="image/*,video/*" multiple onChange={handleDrop} className="hidden" />
              </label>
            )}
          </div>

          {/* Caption Section */}
          <div className="p-4 sm:p-6 border-b border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/60">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm sm:text-base">Caption</h2>
                <p className="text-white/40 text-xs sm:text-sm">Write your post caption</p>
              </div>
            </div>
            <textarea data-testid="dashboard-post-caption" value={content} onChange={(e) => setContent(e.target.value)} placeholder="What's on your mind?" className="w-full h-28 sm:h-32 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-emerald-500 focus:outline-none resize-none text-sm sm:text-base" />
            <div className="flex justify-end mt-2"><span className="text-white/40 text-xs sm:text-sm">{content.length} / 2200</span></div>
          </div>

          {/* Accounts Section */}
          <div className="p-4 sm:p-6 border-b border-white/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <User size={16} className="text-white/60" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-sm sm:text-base">Select Accounts</h2>
                  <p className="text-white/40 text-xs sm:text-sm">Choose accounts to publish to</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 relative">
                <button
                  type="button"
                  onClick={fetchAccounts}
                  data-testid="refresh-accounts-btn"
                  className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/20 text-white/60 text-sm flex items-center gap-1 touch-manipulation select-none"
                  style={{ WebkitTapHighlightColor: 'rgba(255,255,255,0.1)' }}
                  title="Refresh accounts"
                >
                  <span className={loadingAccounts ? 'inline-block animate-spin' : ''}>↻</span>
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <button 
                  type="button"
                  data-testid="open-connect-account-menu"
                  disabled={!!connectingPlatform}
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/20 text-white/60 text-sm flex items-center gap-1 touch-manipulation select-none"
                  style={{ WebkitTapHighlightColor: 'rgba(255,255,255,0.1)' }}
                >
                  {connectingPlatform ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} <span className="hidden sm:inline">{connectingPlatform ? 'Connecting…' : 'Add Account'}</span> <span className="sm:hidden">{connectingPlatform ? 'Connecting…' : 'Add'}</span> <ChevronDown size={14} className={`transition-transform ${showAccountDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showAccountDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-40 bg-black/20" 
                      onClick={() => setShowAccountDropdown(false)} 
                    />
                    <div className="absolute right-0 top-full mt-2 w-72 bg-[#1a1a1a] border border-white/10 rounded-xl p-2 z-50 shadow-2xl">
                      <p className="px-3 py-2 text-white/40 text-xs uppercase tracking-wide">Add a platform</p>
                      {['instagram', 'facebook', 'tiktok', 'youtube', 'x', 'linkedin'].map(platform => (
                        <div 
                          key={platform}
                          role="button"
                          tabIndex={0}
                          data-testid={`connect-${platform}-btn`}
                          onClick={() => handlePlatformSelect(platform)}
                          onKeyDown={(e) => e.key === 'Enter' && handlePlatformSelect(platform)}
                          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg hover:bg-white/10 active:bg-white/20 text-white text-sm capitalize cursor-pointer select-none"
                          style={{ WebkitTapHighlightColor: 'rgba(255,255,255,0.1)' }}
                        >
                          <PlatformIcon platform={platform} size={22} />
                          <span className="font-medium">{platform === 'x' ? 'X (Twitter)' : platform}</span>
                        </div>
                      ))}
                      <div className="mt-2 px-3 py-2 text-white/30 text-[11px] leading-relaxed border-t border-white/5">
                        Sign in and authorise the platform. You’ll return to AutoPostr afterwards.
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {connectError && <div data-testid="social-connect-error" role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm break-words">{connectError}</div>}
            {connectingPlatform && <div data-testid="social-connect-pending" role="status" className="mb-3 text-white/60 text-sm">Opening sign-in…</div>}
            {loadingAccounts ? (
              <div className="flex items-center justify-center py-8"><Loader2 size={24} className="animate-spin text-white/40" /></div>
            ) : accounts.length > 0 ? (
              <div className="space-y-2">
                {accounts.map(account => (
                  <div 
                    key={account.id} 
                    data-testid={`account-row-${account.id}`}
                    className={`w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all touch-manipulation ${selectedAccounts.includes(account.id) ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20 active:bg-white/10'}`}
                  >
                    <button
                      type="button"
                      data-testid={`select-account-${account.id}`}
                      onClick={() => toggleAccount(account.id)}
                      onTouchEnd={(e) => { e.preventDefault(); toggleAccount(account.id); }}
                      className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 text-left"
                    >
                      {account.profile_image_url ? (
                        <img src={account.profile_image_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <PlatformIcon platform={account.platform} size={20} />
                      )}
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-white font-medium text-sm sm:text-base truncate">{account.username || account.display_name || 'Account'}</p>
                        <p className="text-white/40 text-xs sm:text-sm capitalize flex items-center gap-1.5">
                          <PlatformIcon platform={account.platform} size={12} />
                          {account.platform}
                        </p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAccounts.includes(account.id) ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'}`}>
                        {selectedAccounts.includes(account.id) && <Check size={12} className="text-white" />}
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid={`disconnect-${account.id}`}
                      onClick={() => disconnectAccount(account.id, account.platform, account.username || account.display_name || 'Account')}
                      title={`Disconnect ${account.platform}`}
                      className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-white/40 mb-2 text-sm">No accounts connected yet</p>
                <p className="text-white/60 text-xs">Tap "Add" above to connect your social media</p>
              </div>
            )}
          </div>

          {hasTikTok && <div className="p-4 sm:p-6 border-b border-white/5">
            <TikTokPublishOptions prefix="dashboard" mode={tiktokMode} privacy={tiktokPrivacy}
              onModeChange={mode => { setTiktokMode(mode); setTiktokPrivacy(''); }} onPrivacyChange={setTiktokPrivacy} disabled={publishing} />
          </div>}

          {/* Schedule Section */}
          <div className="p-4 sm:p-6 border-b border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center"><Clock size={16} className="text-white/60" /></div>
              <div><h2 className="text-white font-semibold text-sm sm:text-base">When to Post</h2><p className="text-white/40 text-xs sm:text-sm">Publish now or schedule</p></div>
            </div>

            <div className="flex gap-2 sm:gap-3 mb-4">
              <button 
                data-testid="dashboard-send-now" onClick={() => setScheduleMode('now')}
                onTouchEnd={(e) => { e.preventDefault(); setScheduleMode('now'); }}
                className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl border transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base touch-manipulation ${scheduleMode === 'now' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-white/60 hover:border-white/20 active:bg-white/10'}`}
              >
                <Send size={16} className="sm:w-[18px] sm:h-[18px]" /> Post Now
              </button>
              <button 
                data-testid="dashboard-send-later" onClick={() => setScheduleMode('later')}
                onTouchEnd={(e) => { e.preventDefault(); setScheduleMode('later'); }}
                className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl border transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base touch-manipulation ${scheduleMode === 'later' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-white/60 hover:border-white/20 active:bg-white/10'}`}
              >
                <Calendar size={16} className="sm:w-[18px] sm:h-[18px]" /> Schedule
              </button>
            </div>

            {scheduleMode === 'later' && (
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                  <Target size={16} className="text-emerald-400 mt-0.5 sm:w-[18px] sm:h-[18px]" />
                  <div><p className="text-emerald-400 font-medium text-xs sm:text-sm">Schedule Mode</p><p className="text-white/50 text-xs sm:text-sm">Post at your specified time</p></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div><label className="block text-white/60 text-xs sm:text-sm mb-1.5 sm:mb-2">Date</label><input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="w-full p-2.5 sm:p-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-emerald-500 focus:outline-none text-sm sm:text-base" /></div>
                  <div><label className="block text-white/60 text-xs sm:text-sm mb-1.5 sm:mb-2">Time</label><input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="w-full p-2.5 sm:p-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-emerald-500 focus:outline-none text-sm sm:text-base" /></div>
                  <div><label className="block text-white/60 text-xs sm:text-sm mb-1.5 sm:mb-2">Timezone</label><select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full p-2.5 sm:p-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-emerald-500 focus:outline-none appearance-none cursor-pointer text-sm sm:text-base">{timezones.map(tz => (<option key={tz.value} value={tz.value} className="bg-[#1a1a1a]">{tz.label}</option>))}</select></div>
                </div>
              </div>
            )}
          </div>

          {/* Message */}
          {message && (
            <div data-testid="dashboard-post-message" role={message.type === 'error' ? 'alert' : 'status'} className={`mx-4 sm:mx-6 mt-4 p-3 sm:p-4 rounded-xl flex items-center gap-2 sm:gap-3 text-sm sm:text-base ${message.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
              {message.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />} {message.text}
            </div>
          )}

          {/* Publish Button */}
          <div className="p-4 sm:p-6">
            <button 
              data-testid="dashboard-submit-post" onClick={handlePublish}
              onTouchEnd={(e) => { e.preventDefault(); handlePublish(); }}
              disabled={publishing || (!content.trim() && mediaFiles.length === 0) || selectedAccounts.length === 0}
              className="w-full py-3.5 sm:py-4 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base touch-manipulation"
            >
              {publishing ? (<><Loader2 size={18} className="animate-spin sm:w-5 sm:h-5" />{uploadProgress || 'Submitting…'}</>) : (<><Send size={18} className="sm:w-5 sm:h-5" />{draftUpload ? (scheduleMode === 'now' ? 'Send to TikTok drafts' : 'Schedule draft upload') : (scheduleMode === 'now' ? 'Publish Now' : 'Schedule Post')}</>)}
            </button>
            {!isAuthenticated && <p className="text-center text-white/40 text-xs sm:text-sm mt-3">Sign in required to publish</p>}
          </div>
        </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// Sidebar item helper
const SidebarItem = ({ icon, label, active, onClick, testid, badge }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
  >
    <span className={active ? 'text-white' : 'text-white/60'}>{icon}</span>
    <span className="flex-1 text-left font-medium">{label}</span>
    {badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">{badge}</span>}
  </button>
);

export default DashboardPage;
